import React from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  IconButton,
  Collapse,
  Chip,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

export interface EotDetailItem {
  id: string;
  eot_code: string;
  eot_count: number;
  extension_reason: string;
  requested_extension_till: string | null;
  suggested_extension_till: string | null;
  expiry_date: string | null;
  remarks: string | null;
  status_id: number | null;
  status_name: string | null;
  created_at: string;
  updated_at: string;
}

interface Props {
  data?: EotDetailItem[];
  isExpanded: boolean;
  toggleExpand: () => void;
}

/* -------- Date Formatter -------- */
const formatDate = (date?: string | null) => {
  if (!date) return "-";
  const d = new Date(date);
  if (isNaN(d.getTime())) return date;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const EotDetails = ({ data, isExpanded, toggleExpand }: Props) => {
  return (
    <Card
      elevation={0}
      sx={{
        border: "1px solid #E7E6EB",
        borderRadius: "4px",
        overflow: "hidden",
        background: "#fff",
      }}
    >
      {/* Header strip */}
      <Box
        sx={{
          background: "#F4F2F7",
          pl: 2,
          pr: "2px",
          py: 0.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
        }}
        onClick={toggleExpand}
      >
        <Typography
          fontSize={14}
          fontWeight={600}
          className="preview-dropdown-header"
        >
          EOT Details
        </Typography>
        <IconButton aria-label="Collapse section">
          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </Box>

      {/* Expandable Content */}
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: 3 }}>
          {data && data.length > 0 ? (
            data.map((eot, index) => {
              const details = [
                // { label: "EOT Code", value: eot.eot_code },
                // { label: "EOT Count", value: eot.eot_count != null ? String(eot.eot_count) : "-" },
                { label: "Extension Reason", value: eot.extension_reason },
                { label: "Requested Extension Till", value: formatDate(eot.requested_extension_till) },
                { label: "Suggested Extension Till", value: formatDate(eot.suggested_extension_till) },
                { label: "Expiry Date", value: formatDate(eot.expiry_date) },
                { label: "Remarks", value: eot.remarks },
                { label: "Submitted On", value: formatDate(eot.created_at) },
              ];

              return (
                <Box key={eot.id} sx={{ mb: index < data.length - 1 ? 4 : 0 }}>
                  {/* {data.length > 1 && (
                    <Typography fontSize={13} fontWeight={600} mb={1.5} color="#555">
                      EOT #{index + 1}
                    </Typography>
                  )} */}

                  {/* Status chip */}
                  {eot.status_name && (
                    <Box mb={2}>
                      <Chip
                        label={eot.status_name}
                        size="small"
                        sx={{
                          backgroundColor: "#EDE7F6",
                          color: "#512DA8",
                          fontWeight: 600,
                          fontSize: 12,
                        }}
                      />
                    </Box>
                  )}

                  <Grid container rowSpacing={2.5} columnSpacing={6}>
                    {details.map((item, i) => (
                      <Grid item xs={12} sm={6} key={i}>
                        <Typography sx={{ color: "#8E8E93", fontSize: 12, mb: 0.5 }}>
                          {item.label}
                        </Typography>
                        <Typography sx={{ color: "#111", fontSize: 14, fontWeight: 500 }}>
                          {item.value || "-"}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>

                  {index < data.length - 1 && (
                    <Box sx={{ borderBottom: "1px solid #E7E6EB", mt: 3 }} />
                  )}
                </Box>
              );
            })
          ) : (
            <Typography sx={{ color: "#8E8E93", fontSize: 14 }}>
              No EOT details available
            </Typography>
          )}
        </Box>
      </Collapse>
    </Card>
  );
};
